import { useState, useCallback } from 'react';
import { reportService } from '../services';
import { WellbeingReportsResponse, ReportsParams, ProcessedWellbeingData } from '../types/reports';
import { useAuth } from '../context/AuthContext';
import { processWellbeingData } from '../utils/wellbeingDataProcessor';

export interface UseWellbeingReportsState {
  data: ProcessedWellbeingData | null;
  rawData: WellbeingReportsResponse | null;
  loading: boolean;
  error: string | null;
}

export interface UseWellbeingReportsResult extends UseWellbeingReportsState {
  fetchReports: (params?: ReportsParams) => Promise<void>;
  refetch: () => Promise<void>;
}

export const useWellbeingReports = (initialParams?: ReportsParams): UseWellbeingReportsResult => {
  const { isAuthenticated, user } = useAuth();
  const [state, setState] = useState<UseWellbeingReportsState>({
    data: null,
    rawData: null,
    loading: false,
    error: null,
  });

  const [currentParams, setCurrentParams] = useState<ReportsParams>(initialParams || {});

  const fetchReports = useCallback(async (params: ReportsParams = {}) => {
    if (!isAuthenticated || !user?.roles?.includes('reportsReader')) {
      setState(prev => ({ 
        ...prev, 
        error: 'Access denied: reportsReader role required',
        loading: false 
      }));
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    setCurrentParams(params);

    try {
      const rawData = await reportService.getWellbeingReports(params);
      
      // Process the raw data into organized structure
      // Pass the requested date range to ensure chart uses filter dates, not response dates
      const requestedDateRange = params.startDay && params.endDay 
        ? { startDay: params.startDay, endDay: params.endDay }
        : undefined;
      const processedData = processWellbeingData(rawData, requestedDateRange);
      
      setState(prev => ({ 
        ...prev, 
        data: processedData,
        rawData,
        loading: false 
      }));
    } catch (error) {
      setState(prev => ({ 
        ...prev, 
        error: error instanceof Error ? error.message : 'Failed to fetch wellbeing reports',
        loading: false 
      }));
    }
  }, [isAuthenticated, user?.roles]);

  const refetch = useCallback(() => {
    return fetchReports(currentParams);
  }, [fetchReports, currentParams]);

  // Note: Initial fetch is handled by parent Reports page via handleApplyFilter()
  // No auto-fetch on mount to avoid race conditions and inconsistent data

  return {
    ...state,
    fetchReports,
    refetch,
  };
};